/**
 * MutationResultCard — Generic inline card for tool mutation results.
 *
 * Rendered in the CopilotKit chat when the backend emits a `show_mutation_result`
 * frontend action. Displays the created/updated entity with empty field indicators,
 * suggestions, and contextual next-action buttons.
 *
 * Delegates entity-specific rendering to sub-renderers based on `type`.
 */
import React, { useState } from 'react';
import { useChatAction } from './ChatActionContext';

// ── Entity-specific renderers ─────────────────────────────────────────

const OfferingRenderer: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <div className="space-y-2">
    <div className="flex items-start gap-3">
      {data.image_url ? (
        <img src={data.image_url} alt={data.name} className="w-12 h-12 rounded-lg object-cover" />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 text-lg">
          🛍
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">{data.name}</p>
        {data.price != null && (
          <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
            {data.price} {data.currency || 'EUR'}
          </p>
        )}
      </div>
      {data.category && (
        <span className="inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          {data.category}
        </span>
      )}
    </div>
    {data.description && (
      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{data.description}</p>
    )}
  </div>
);

const RuleRenderer: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <div className="space-y-2">
    <p className="font-semibold text-sm text-slate-900 dark:text-white">{data.name}</p>
    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
      {data.scope && (
        <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
          {data.scope}
        </span>
      )}
      {data.is_hard_constraint && (
        <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          Hard
        </span>
      )}
      {data.priority != null && data.priority !== 0 && (
        <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          P{data.priority}
        </span>
      )}
    </div>
    {data.condition_text && (
      <div className="text-xs space-y-1">
        <p className="text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-700 dark:text-slate-200">When: </span>
          {data.condition_text.slice(0, 100)}{data.condition_text.length > 100 ? '...' : ''}
        </p>
        {data.action_text && (
          <p className="text-slate-500 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-200">Then: </span>
            {data.action_text.slice(0, 100)}{data.action_text.length > 100 ? '...' : ''}
          </p>
        )}
      </div>
    )}
  </div>
);

const ScenarioRenderer: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const steps = data.steps || [];
  return (
    <div className="space-y-2">
      <p className="font-semibold text-sm text-slate-900 dark:text-white">{data.name}</p>
      {data.entry_condition_text && (
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">
          Trigger: {data.entry_condition_text.slice(0, 80)}
        </p>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        {steps.map((step: any, i: number) => (
          <React.Fragment key={step.id || i}>
            <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${
              step.is_entry ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : step.is_terminal ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}>
              {step.name}
            </span>
            {i < steps.length - 1 && <span className="text-slate-300 dark:text-slate-600 text-xs">&rarr;</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ProfileRenderer: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const identity = data.identity || {};
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 text-sm font-bold">
        {identity.display_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-sm text-slate-900 dark:text-white">{identity.display_name || 'Agent'}</p>
        {identity.tagline && (
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{identity.tagline}</p>
        )}
        {identity.industry && (
          <span className="inline-flex px-1.5 py-0.5 mt-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {identity.industry}
          </span>
        )}
      </div>
    </div>
  );
};

const DeployRenderer: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <div className="space-y-1.5">
    {data.passed != null && (
      <div className={`flex items-center gap-2 text-sm font-medium ${data.passed ? 'text-emerald-600' : 'text-red-600'}`}>
        <span>{data.passed ? '✓' : '✗'}</span>
        <span>{data.passed ? 'Validation passed' : 'Validation failed'}</span>
      </div>
    )}
    {(data.issues || []).map((issue: string, i: number) => (
      <p key={i} className="text-xs text-red-500 flex items-center gap-1">
        <span>✗</span> {issue}
      </p>
    ))}
    {(data.warnings || []).map((w: string, i: number) => (
      <p key={i} className="text-xs text-amber-500 flex items-center gap-1">
        <span>⚠</span> {w}
      </p>
    ))}
    {data.deployed && (
      <p className="text-xs text-emerald-600 font-medium">Configuration deployed and live.</p>
    )}
  </div>
);

const GenericRenderer: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <div className="space-y-1">
    {Object.entries(data).filter(([k]) => !k.startsWith('_')).slice(0, 6).map(([key, value]) => (
      <div key={key} className="flex items-center justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">{key}</span>
        <span className="text-slate-900 dark:text-white font-medium truncate max-w-[180px]">
          {typeof value === 'object' ? JSON.stringify(value).slice(0, 40) : String(value)}
        </span>
      </div>
    ))}
  </div>
);

// ── Renderer mapping ──────────────────────────────────────────────────

const RENDERERS: Record<string, React.FC<{ data: Record<string, any> }>> = {
  offering: OfferingRenderer,
  rule: RuleRenderer,
  scenario: ScenarioRenderer,
  profile: ProfileRenderer,
  deploy: DeployRenderer,
};

// ── Action labels ─────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  enable: 'Enabled',
  disable: 'Disabled',
  deploy: 'Deployed',
  validate: 'Validated',
  compile: 'Compiled',
  bulk_create: 'Bulk created',
  setup_sms: 'SMS set up',
  setup_webchat: 'Webchat enabled',
  setup_whatsapp: 'WhatsApp setup',
  connect: 'Connected',
};

const ENTITY_LABELS: Record<string, string> = {
  offering: 'Offering',
  rule: 'Rule',
  scenario: 'Scenario',
  profile: 'Profile',
  channel: 'Channel',
  voice: 'Voice',
  integration: 'Integration',
  deploy: 'Deploy',
  variable: 'Variable',
  template: 'Template',
};

// ── Main component ────────────────────────────────────────────────────

export interface MutationResultCardProps {
  type: string;
  action?: string;
  data: Record<string, any>;
  emptyFields?: string[];
  suggestions?: Record<string, string>;
  nextActions?: string[];
}

export const MutationResultCard: React.FC<MutationResultCardProps> = ({
  type,
  action = 'create',
  data,
  emptyFields = [],
  suggestions = {},
  nextActions = [],
}) => {
  const [dismissed, setDismissed] = useState(false);
  const { sendChatMessage } = useChatAction();

  if (dismissed) return null;

  const Renderer = RENDERERS[type] || GenericRenderer;
  const actionLabel = ACTION_LABELS[action] || action;
  const entityLabel = ENTITY_LABELS[type] || type;
  const entityName = data.name || data.display_name || '';

  // Filter empty fields that have suggestions
  const suggestedFields = emptyFields.filter((f) => suggestions[f]);
  const otherEmpty = emptyFields.filter((f) => !suggestions[f]);

  return (
    <div className="my-2 rounded-2xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02]">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {actionLabel} {entityLabel}
        </span>
        <button
          onClick={() => setDismissed(true)}
          className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
        >
          ✓ OK
        </button>
      </div>

      {/* Entity content */}
      <div className="px-3 py-2.5">
        <Renderer data={data} />
      </div>

      {/* Suggested fields */}
      {suggestedFields.length > 0 && (
        <div className="px-3 py-2 border-t border-black/5 dark:border-white/5 bg-amber-50/50 dark:bg-amber-900/10">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1.5">
            Suggestions
          </p>
          {suggestedFields.map((field) => (
            <div key={field} className="flex items-center justify-between text-xs mb-1 gap-1">
              <span className="text-slate-500 dark:text-slate-400 shrink-0">{field.replace(/\./g, ' > ')}</span>
              <span className="text-amber-600 dark:text-amber-400 italic truncate max-w-[120px]">
                {suggestions[field]}
              </span>
              <button
                onClick={() => {
                  const fieldLabel = field.replace(/\./g, ' ');
                  sendChatMessage(`Met à jour ${fieldLabel} de ${entityName || type} à "${suggestions[field]}"`);
                }}
                className="shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
              >
                Apply
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Other empty fields */}
      {otherEmpty.length > 0 && (
        <div className="px-3 py-1.5 border-t border-black/5 dark:border-white/5">
          <p className="text-[10px] text-slate-400 dark:text-slate-500">
            Empty: {otherEmpty.join(', ')}
          </p>
        </div>
      )}

      {/* Next actions */}
      {nextActions.length > 0 && (
        <div className="px-3 py-2 border-t border-black/5 dark:border-white/5 flex flex-wrap gap-1.5">
          {nextActions.map((na) => (
            <button
              key={na}
              onClick={() => sendChatMessage(na)}
              className="text-[10px] px-2 py-1 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              {na}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MutationResultCard;
